import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SearchBar } from "@/components/SearchBar";
import { api, prefetchSearch } from "@/lib/api";
import { ArrowRight, Compass, Sparkles, Wallet, Heart, ShieldCheck } from "lucide-react";

const HERO = "https://images.pexels.com/photos/29172648/pexels-photo-29172648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

const Step = ({ n, title, body, icon: Icon }) => (
    <div className="relative">
        <span className="overline">step {n}</span>
        <div className="mt-3 flex items-start gap-4">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-jade/10 text-jade">
                <Icon size={20} />
            </span>
            <div>
                <h3 className="font-display text-2xl text-ink">{title}</h3>
                <p className="mt-1 text-moss text-sm leading-relaxed">{body}</p>
            </div>
        </div>
    </div>
);

const DestinationTile = ({ d, span = "" }) => (
    <Link
        to={`/explore?destination=${encodeURIComponent(d.name)}&days=5&travelers=2&budget_level=mid`}
        onMouseEnter={() => prefetchSearch({ destination: d.name })}
        onFocus={() => prefetchSearch({ destination: d.name })}
        data-testid={`popular-${d.name.toLowerCase()}`}
        className={`group relative overflow-hidden rounded-3xl ${span}`}
    >
        <img src={d.image} alt={d.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-5">
            <span className="rounded-full bg-sand/90 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-jade">{d.tag}</span>
            <h3 className="mt-3 font-display text-3xl text-sand">{d.name}</h3>
            <p className="flex items-center justify-between text-sand/80">
                <span className="text-sm">{d.country}</span>
                <span className="text-xs">from <span className="font-display text-lg">${d.price_from}</span></span>
            </p>
        </div>
    </Link>
);

export default function Home() {
    const [popular, setPopular] = useState([]);

    useEffect(() => {
        api.get("/popular-destinations").then(({ data }) => setPopular(data.destinations || [])).catch(() => {});
    }, []);

    return (
        <div className="App">
            <Navbar />

            {/* HERO */}
            <section className="relative min-h-[88vh] overflow-hidden">
                <img src={HERO} alt="Coastal travel inspiration" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/40 via-ink/15 to-sand" />
                <div className="relative mx-auto flex min-h-[88vh] max-w-6xl flex-col justify-end px-6 pb-10 pt-32">
                    <div className="max-w-3xl animate-fade-up">
                        <span className="overline text-sand/90">your atlas, simplified</span>
                        <h1 className="mt-4 font-display text-5xl font-medium leading-[1.02] tracking-tight text-sand sm:text-6xl md:text-7xl">
                            Plan the trip
                            <br />
                            <em className="not-italic text-terracotta">you already imagined.</em>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg font-light text-sand/85">
                            One search. Real costs. Hand-picked hotels, restaurants and things to do —
                            curated by AI, finished by you.
                        </p>
                    </div>
                </div>
            </section>

            {/* SEARCH (overlapping hero) */}
            <section id="explore" className="relative z-10 mx-auto -mt-20 max-w-6xl px-6">
                <SearchBar />
                <div className="mt-3 flex flex-wrap items-center gap-2 px-2 text-xs text-moss">
                    <span className="overline !text-moss/70">trending</span>
                    {["Tokyo", "Paris", "Bali", "Cape Town", "Lisbon", "Mexico City"].map((c) => (
                        <Link
                            key={c}
                            to={`/explore?destination=${c}&days=5&travelers=2&budget_level=mid`}
                            onMouseEnter={() => prefetchSearch({ destination: c })}
                            onFocus={() => prefetchSearch({ destination: c })}
                            className="rounded-full bg-white/60 px-3 py-1 ring-1 ring-jade/10 hover:bg-terracotta hover:text-sand transition"
                        >
                            {c}
                        </Link>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="how" className="mx-auto mt-32 max-w-6xl px-6">
                <div className="grid gap-12 md:grid-cols-[1fr_2fr]">
                    <div>
                        <span className="overline">why werighty</span>
                        <h2 className="mt-3 font-display text-4xl text-ink md:text-5xl">Travel research, finally in one breath.</h2>
                    </div>
                    <div className="grid gap-10 md:grid-cols-3">
                        <Step n="1" title="Tell us where" icon={Compass} body="Type a city, region or country. No filters. No friction." />
                        <Step n="2" title="See real costs" icon={Wallet} body="Realistic min–max ranges based on 2024-2025 market data. Not exaggerated." />
                        <Step n="3" title="Save the best" icon={Heart} body="Pin hotels, restaurants and activities. Build your perfect day." />
                    </div>
                </div>
                <div className="mt-12 flex flex-wrap items-center gap-3 rounded-3xl bg-white p-5 ring-1 ring-jade/10">
                    <span className="grid h-10 w-10 place-items-center rounded-full bg-jade/10 text-jade"><ShieldCheck size={18} /></span>
                    <p className="text-sm text-moss">
                        <b className="text-ink">Realistic budgets, no surprises.</b> Our cost ranges blend a curated 2024-2025 price database (Numbeo / Budgetyourtrip aggregates) with live USD exchange rates — and an AI curator on top for hotels, restaurants and activities.
                    </p>
                </div>
            </section>

            {/* POPULAR */}
            <section id="popular" className="mx-auto mt-32 max-w-6xl px-6">
                <div className="flex items-end justify-between">
                    <div>
                        <span className="overline">where the world is going</span>
                        <h2 className="mt-3 font-display text-4xl text-ink md:text-5xl">Six destinations we love this season.</h2>
                    </div>
                    <a href="#explore" className="hidden text-sm text-jade hover:text-terracotta md:inline-flex items-center gap-1">Search your own <ArrowRight size={14} /></a>
                </div>

                {popular.length > 0 && (
                    <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-6 md:grid-rows-2 md:h-[640px]">
                        <DestinationTile d={popular[0]} span="md:col-span-3 md:row-span-2" />
                        <DestinationTile d={popular[1]} span="md:col-span-3" />
                        <DestinationTile d={popular[2]} span="md:col-span-2" />
                        <DestinationTile d={popular[3]} span="md:col-span-1" />
                        {popular[4] && <DestinationTile d={popular[4]} span="md:col-span-2 md:hidden" />}
                        {popular[5] && <DestinationTile d={popular[5]} span="md:col-span-2 md:hidden" />}
                    </div>
                )}
            </section>

            {/* CTA */}
            <section className="mx-auto mt-32 max-w-6xl px-6">
                <div className="relative overflow-hidden rounded-[2.5rem] bg-ink p-10 md:p-16">
                    <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-terracotta/20 blur-3xl" />
                    <div className="absolute -left-12 -bottom-12 h-60 w-60 rounded-full bg-jade/30 blur-3xl" />
                    <div className="relative grid gap-10 md:grid-cols-2 md:items-center">
                        <div>
                            <span className="overline text-sand/80">free forever</span>
                            <h2 className="mt-3 font-display text-4xl text-sand md:text-5xl">A travel agent in your pocket — without the fees.</h2>
                            <p className="mt-4 max-w-lg text-sand/70">Create an account to save favourites, revisit search history and craft itineraries you can actually book.</p>
                        </div>
                        <div className="flex flex-wrap gap-3 md:justify-end">
                            <Link to="/register" data-testid="cta-register" className="btn-primary !px-7 !py-4 text-base">
                                <Sparkles size={18} className="mr-2" /> Create my account
                            </Link>
                            <Link to="/login" data-testid="cta-login" className="pill border border-sand/30 px-7 py-4 text-sand hover:bg-sand/10">
                                Login
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
